// frontend/src/contexts/QCMContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    fetchQCMContent,
    primeLlmContext,
    evaluateAnswer,
    pythonApi,
    scoringApi,
    nodeApi,
    fetchPrimingPrompt,
    fetchPromptsConfig,
    SEARCH_QCM_LAB_API_BASE_URL,
    authHeaders,
    encodeExamIdForUrl,
} from '../services/api';
import {
    NO_QUESTION_FOR_STRATEGY_MESSAGE,
    getMergedRevisionStrategyFromStorage,
    pickQcmIdForUnderpracticedTopics,
    postRevisionQcm,
    applyRandomRevisionStrategyWhenLeftTopicsExhausted,
    shouldShowLeftTopicsRandomFallbackNotice,
    LEFT_TOPICS_RANDOM_FALLBACK_NOTICE_EN,
} from '../utils/qcmInitialLoad';
import { useTranslation } from 'react-i18next';

const QCMContext = createContext();

const Z_QCM_BLOCKING = 999998;

/**
 * Réponses load_item hétérogènes (QCM, FlashCard_Tips, vieux docs) : garantir qcm_id + bloc qcm minimal.
 * Évite « Format non reconnu » puis écran noir si seul Tips / FlashCard_Tips_id est présent.
 */
function normalizeSearchLoadItemPayload(data) {
    if (!data || typeof data !== 'object') return data;
    const out = { ...data };
    if (out.questionType === 'funnel-qcm') {
        return out;
    }
    const qid =
        out.qcm_id ||
        out.FlashCard_Tips_id ||
        (out._id != null ? String(out._id) : '') ||
        (out.id != null && typeof out.id !== 'object' ? String(out.id) : '');
    if (qid && !out.qcm_id) {
        out.qcm_id = String(qid);
    }
    const tips = out.Tips && typeof out.Tips === 'object' ? out.Tips : {};
    const prob = String(tips.ProblemToResolve || '').trim();
    const sol = String(tips.Solution || '').trim();
    const flashLike =
        out.questionType === 'FlashCard_Tips' || Boolean(prob || sol);
    if (flashLike && (!out.qcm || typeof out.qcm !== 'object')) {
        const kws = Array.isArray(tips.keywords) ? tips.keywords : [];
        out.qcm = {
            question: prob,
            correct_answer: [{ answer: sol, explanation: '', keywords: kws }],
            distractors: [],
        };
    } else if (flashLike && out.qcm && typeof out.qcm === 'object' && !String(out.qcm.question || '').trim() && prob) {
        out.qcm = {
            ...out.qcm,
            question: prob,
            correct_answer: Array.isArray(out.qcm.correct_answer)
                ? out.qcm.correct_answer
                : out.qcm.correct_answer
                  ? [out.qcm.correct_answer]
                  : [{ answer: sol, explanation: '' }],
        };
    }
    return out;
}

export const useQCM = () => {
    return useContext(QCMContext);
};

export const QCMProvider = ({
    children,
    sessionContext: externalSessionContext,
    initialQcmId,
    initialNoQuestionForStrategy = false,
}) => {
    const { t } = useTranslation();
    
    // --- QCM Data State ---
    const [qcmData, setQcmData] = useState(null);
    const [currentQCMIndex, setCurrentQCMIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [evaluationLoading, setEvaluationLoading] = useState(false);
    const [error, setError] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [currentQuestionType, setCurrentQuestionType] = useState('qcm'); // Mode QCM par défaut

    // --- Funnel QCM State ---
    const [currentStep, setCurrentStep] = useState(1); // Étape courante pour les QCM Funnel
    const [completedSteps, setCompletedSteps] = useState(new Set()); // Étapes complétées
    const [totalSteps, setTotalSteps] = useState(0); // Nombre total d'étapes
    const [availableSteps, setAvailableSteps] = useState([]); // Liste des étapes disponibles
    const [isLastStep, setIsLastStep] = useState(false); // Si c'est la dernière étape

    // --- QCM-specific State ---
    const [freeTextAnswer, setFreeTextAnswer] = useState(''); // Réponse libre de l'utilisateur
    const [selectedOption, setSelectedOption] = useState(''); // Option QCM sélectionnée
    const [degreeOfCertainty, setDegreeOfCertainty] = useState(''); // low, medium, high
    const [hoveredCertainty, setHoveredCertainty] = useState({}); // { option: index, level: 'low'|'medium'|'high' }
    const [conceptualMappingAnswers, setConceptualMappingAnswers] = useState({}); // Drag & drop answers
    
    // --- Display and Navigation State ---
    const [displayMode, setDisplayMode] = useState('question'); // question, qcm, solution, help
    const [qcmSolutionContent, setQcmSolutionContent] = useState(null);
    const [showSummary, setShowSummary] = useState(false);
    const [showKnowledgeReinforcement, setShowKnowledgeReinforcement] = useState(false);
    
    // --- Timing and Attempts ---
    const [attemptCount, setAttemptCount] = useState(1);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [maxTime, setMaxTime] = useState(0);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const elapsedTimeRef = useRef(0);

    // --- LLM V2 (QCM) priming persistence ---
    const [qcmPrimingMessages, setQcmPrimingMessages] = useState([]);
    const [qcmSlotId, setQcmSlotId] = useState(null);
    const primedQcmIdRef = useRef(null);
    const primingInFlightRef = useRef(false);
    
    // --- Navigation Options ---
    const [options, setOptions] = useState([]);
    const [showNavigationButton, setShowNavigationButton] = useState(false);
    const [navigationButtonLabel, setNavigationButtonLabel] = useState('');
    const [navigationButtonAction, setNavigationButtonAction] = useState(null);

    // --- Funnel user selections (per option: OK/KO and confidence) ---
    const [funnelUserChoices, setFunnelUserChoices] = useState({}); // { [answer: string]: { choice: 'OK'|'KO', confidence: 'low'|'medium'|'high' } }
    const [lastFunnelResult, setLastFunnelResult] = useState(null);

    // --- Session Context ---
    const sessionContext = externalSessionContext || {
        sessionId: 'default_session',
        user: 'default_user',
        exam: 'generative-ai-leader',
        strategy: 'smart_test1'
    };


    // Current QCM data - Adapter la structure selon le format des données
    const currentQCM = qcmData?.questions 
        ? qcmData.questions[currentQCMIndex]  // Format ancien (tableau de questions)
        : qcmData;                            // Format nouveau (QCM unique depuis l'API)

    // Debug logging
    React.useEffect(() => {
        console.log('🔥 DEBUG QCMProvider - sessionContext:', sessionContext);
        console.log('🔥 DEBUG QCMProvider - initialQcmId:', initialQcmId);
        console.log('🔥 DEBUG QCMProvider - currentQCM:', currentQCM);
    }, [sessionContext, initialQcmId, currentQCM]);

    /** SmartExamRouter : stratégie sans aucun QCM → erreur + pas de spinner bloquant. */
    useEffect(() => {
        if (initialNoQuestionForStrategy) {
            setError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
            setLoading(false);
        }
    }, [initialNoQuestionForStrategy]);

    /** Pas d’ID initial (sans bootstrap « no question ») : éviter loading infini. */
    useEffect(() => {
        if (!initialQcmId && !initialNoQuestionForStrategy) {
            setLoading(false);
        }
    }, [initialQcmId, initialNoQuestionForStrategy]);

    // --- Timer Management ---
    useEffect(() => {
        let timer;
        if (isTimerRunning) {
            timer = setInterval(() => {
                elapsedTimeRef.current += 1;
                setElapsedTime(elapsedTimeRef.current);
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [isTimerRunning]);

    // --- Initialize timer when QCM data is loaded - only after full load ---
    useEffect(() => {
        if (currentQCM && qcmData && !loading) {
            if (currentQCM.metadata && currentQCM.metadata.execution_time) {
                // Parse execution_time - can be string like "2 minutes" or number
                let timeInMinutes = 0;
                if (typeof currentQCM.metadata.execution_time === 'string') {
                    // Extract number from string like "2 minutes"
                    const match = currentQCM.metadata.execution_time.match(/(\d+)/);
                    timeInMinutes = match ? parseInt(match[1]) : 5; // Default 5 minutes
                } else if (typeof currentQCM.metadata.execution_time === 'number') {
                    timeInMinutes = currentQCM.metadata.execution_time;
                }
                
                if (timeInMinutes > 0) {
                    const maxTimeSeconds = timeInMinutes * 60;
                    setMaxTime(maxTimeSeconds);
                    // Only start timer if not already running and not in solution mode
                    if (!isTimerRunning && displayMode !== 'solution') {
                        setIsTimerRunning(true);
                    }
                } else {
                    setMaxTime(0);
                    setIsTimerRunning(false);
                }
            } else {
                setMaxTime(0);
                setIsTimerRunning(false);
            }
        }
    }, [currentQCM, qcmData, loading, isTimerRunning, displayMode]);

    // --- Timer reset when switching to QCM mode ---
    useEffect(() => {
        if ((displayMode === 'qcm' || currentQCM?.questionType === 'funnel-qcm') && currentQCM?.metadata?.execution_time) {
            // Reset timer when switching to QCM mode or for Funnel QCMs
            setElapsedTime(0);
            elapsedTimeRef.current = 0;
            setIsTimerRunning(true);
        }
    }, [displayMode, currentQCM]);

    // --- Stop timer when solution is displayed for Funnel QCMs ---
    useEffect(() => {
        if (currentQCM?.questionType === 'funnel-qcm' && displayMode === 'solution') {
            setIsTimerRunning(false);
        }
    }, [displayMode, currentQCM]);

    // --- QCM Priming (V2) ---
    const primeQcmContext = useCallback(async () => {
        if (!currentQCM) return;
        try {
            // Build priming prompt from IaGen_OpenAnswer_Evaluation_priming
            const promptsConfig = await fetchPromptsConfig();
            const primingCfg = promptsConfig?.IaGen_OpenAnswer_Evaluation_priming;
            const examTitle = promptsConfig?.examTitle || (qcmData?.exam_title || 'Exam');
            const editor = promptsConfig?.editor || '';
            const persona = primingCfg?.persona || '';
            const task = primingCfg?.task || '';
            const outputFormat = primingCfg?.output_format || '';
            const inputPlaceholders = primingCfg?.input_placeholders || '';
            const primingPrompt = [
                (persona || '').replace('[exam_name]', examTitle).replace('[editor]', editor),
                task || '',
                outputFormat || '',
                (inputPlaceholders || '').replace('[expected_solution]', currentQCM.qcm?.correct_answer?.answer || '')
            ].filter(Boolean).join('\n\n');

            const initialHistory = [
                { role: 'user', content: primingPrompt }
            ];

            const payload = {
                userId: sessionContext.user,
                sessionId: sessionContext.sessionId,
                examId: sessionContext.exam,
                instruction_id: currentQCM.qcm_id,
                sub_instruction_id: currentQCM.qcm_id,
                sub_instruction_context: currentQCM.context || '',
                sub_instruction_description: currentQCM.qcm?.correct_answer?.answer || '',
                examType: 'qcm',
                context: sessionContext, // Ajout de la clé context dans le payload
                conversation_history: initialHistory
            };

            const primeResp = await primeLlmContext(payload);
            if (primeResp && primeResp.success) {
                const assistantAck = (primeResp.response && typeof primeResp.response === 'string')
                    ? primeResp.response.trim() || 'OKI'
                    : 'OKI';
                // Persist exactly two messages: priming user + assistant OKI
                const twoMessageHistory = [
                    initialHistory[0],
                    { role: 'assistant', content: assistantAck }
                ];
                setQcmPrimingMessages(twoMessageHistory);
                setQcmSlotId(typeof primeResp.slot_id !== 'undefined' ? primeResp.slot_id : null);
                primedQcmIdRef.current = currentQCM.qcm_id;
                return { messages: twoMessageHistory, slotId: primeResp.slot_id ?? null };
            } else {
                // Backend responded but not success → build fallback 2-message history
                const fallbackHistory = [
                    initialHistory[0],
                    { role: 'assistant', content: 'OKI' }
                ];
                setQcmPrimingMessages(fallbackHistory);
                setQcmSlotId(null);
                primedQcmIdRef.current = currentQCM.qcm_id;
                return { messages: fallbackHistory, slotId: null };
            }
        } catch (e) {
            console.error('QCM priming failed:', e);
            // Fallback: keep a minimal 2-message history so evaluation doesn't 400
            try {
                const promptsConfig = await fetchPromptsConfig();
                const primingCfg = promptsConfig?.IaGen_OpenAnswer_Evaluation_priming || {};
                const examTitle = promptsConfig?.examTitle || (qcmData?.exam_title || 'Exam');
                const editor = promptsConfig?.editor || '';
                const persona = primingCfg?.persona || '';
                const task = primingCfg?.task || '';
                const outputFormat = primingCfg?.output_format || '';
                const inputPlaceholders = primingCfg?.input_placeholders || '';
                const primingPrompt = [
                    (persona || '').replace('[exam_name]', examTitle).replace('[editor]', editor),
                    task || '',
                    outputFormat || '',
                    (inputPlaceholders || '').replace('[expected_solution]', currentQCM?.qcm?.correct_answer?.answer || '')
                ].filter(Boolean).join('\n\n');
                const fallbackHistory = [
                    { role: 'user', content: primingPrompt },
                    { role: 'assistant', content: 'OKI' }
                ];
                setQcmPrimingMessages(fallbackHistory);
                return { messages: fallbackHistory, slotId: null };
            } catch (_ignored) {
                // If even template fails, set a generic fallback
                const genericHistory = [
                    { role: 'user', content: 'Priming context for QCM.' },
                    { role: 'assistant', content: 'OKI' }
                ];
                setQcmPrimingMessages(genericHistory);
                return { messages: genericHistory, slotId: null };
            }
        }
    }, [currentQCM, sessionContext]);

    // Trigger priming when a new QCM is shown in question mode
    useEffect(() => {
        if (!currentQCM) return;
        if (displayMode !== 'question') return;
        if (primedQcmIdRef.current === currentQCM.qcm_id) return;
        primeQcmContext();
    }, [currentQCM, displayMode, primeQcmContext]);

    // --- Navigation Options Management ---
    const updateNavigationOptions = useCallback(() => {
        const opts = [];

        // Solution mode: no QCM/help buttons; show Next Step or Next QCM for funnel via events
        if (displayMode === 'solution') {
            if (currentQCM?.questionType === 'funnel-qcm') {
                // For funnel QCMs, only show Next QCM button on the last step
                if (isLastStep) {
                    opts.push({ id: 'next_qcm', label: t('next_qcm') || 'Next QCM', action: () => {
                        window.dispatchEvent(new CustomEvent('requestNextQCM'));
                    }});
                }
                // For non-last steps in funnel QCMs, don't show any navigation button here
                // (NextStepButton component handles this)
            } else {
                // For regular QCMs, always show Next QCM button
                opts.push({ id: 'next_qcm', label: t('next_qcm') || 'Next QCM', action: () => {
                    window.dispatchEvent(new CustomEvent('requestNextQCM'));
                }});
            }
            setOptions(opts);
            return;
        }

        // QCM mode (options visible): hide QCM button itself; show Solution and Help
        if (displayMode === 'qcm') {
            opts.push({ id: 'solution', label: t('solution') || 'Solution', action: () => setDisplayMode('solution') });
            opts.push({ id: 'help', label: t('help') || 'Help', action: () => setDisplayMode('help') });
            setOptions(opts);
            return;
        }

        // Help or Question mode: show Options button to go back to options
        opts.push({ id: 'qcm', label: t('options') || 'Options', action: () => {
            setDisplayMode('qcm');
            setDegreeOfCertainty('');
        }});
        setOptions(opts);
    }, [displayMode, t, currentQCM, isLastStep]);

    // Keep navigation options in sync with display mode and QCM changes
    useEffect(() => {
        updateNavigationOptions();
    }, [displayMode, currentQCM, isLastStep, updateNavigationOptions]);

    // --- Funnel QCM Step Loading ---
    const loadFunnelQCMStep = useCallback(async (qcmId, stepNumber) => {
        setLoading(true);
        setError(null);
        
        try {
            console.log(`🔍 Loading Funnel QCM step ${stepNumber} for ID: ${qcmId}`);
            
            // Utiliser l'API de recherche (port 5009) au lieu de l'API Node.js (port 5001)
            const response = await fetch(`${SEARCH_QCM_LAB_API_BASE_URL}/api/search/load_item`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({
                    id: qcmId,
                    userid: 'anonymous_user',
                    questionType: 'qcm'
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                const stepData = data.item;
                console.log('📋 Funnel QCM step data received:', stepData);
                
                // Set the QCM data with the specific step
                setQcmData(stepData);
                
                // Update funnel-specific state - Use the actual step number from dataset
                setCurrentStep(stepNumber);
                setTotalSteps(stepData.total_steps);
                setAvailableSteps(stepData.available_steps);
                setIsLastStep(stepData.is_last_step);
                
                console.log(`✅ Set currentStep to ${stepNumber} from dataset`);
                
                // Detect and update questionType if necessary
                if (stepData.questionType && stepData.questionType !== currentQuestionType) {
                    setCurrentQuestionType(stepData.questionType);
                }
                
                setCurrentQCMIndex(0);
                setDisplayMode('question');
                
                // Initialize navigation options
                updateNavigationOptions();

                // Reset priming state for new step
                primedQcmIdRef.current = null;
                setQcmPrimingMessages([]);
                setQcmSlotId(null);
                // Reset funnel user choices for the new step
                setFunnelUserChoices({});
                setLastFunnelResult(null);
                
                // Reset QCM selection states for new step
                setSelectedOption('');
                setDegreeOfCertainty('');
                setFreeTextAnswer('');
                setConceptualMappingAnswers({});
                
            } else {
                throw new Error(response.data.message || 'Failed to load funnel QCM step');
            }
            
        } catch (err) {
            console.error('Error loading funnel QCM step:', err);
            setError(err.message || 'Erreur lors du chargement de l\'étape du QCM Funnel');
        } finally {
            setLoading(false);
        }
    }, [currentQuestionType, updateNavigationOptions]);

    /** Après « Next » ou chargement volontaire : ne plus réappliquer initialQcmId. */
    const initialSnapResolvedRef = useRef(false);
    useEffect(() => {
        initialSnapResolvedRef.current = false;
    }, [initialQcmId]);

    // --- QCM Content Loading ---
    const loadQCMContent = useCallback(async (qcmId, options = {}) => {
        const { markInitialSnapResolved = false } = options;
        setLoading(true);
        setError(null);
        
        try {
            console.log('🔥 Loading QCM content for ID:', qcmId);
            
            // First, get basic QCM info to check if it's a funnel QCM
            const raw = await fetchQCMContent(qcmId);
            const data = normalizeSearchLoadItemPayload(raw);
            console.log('📋 QCM data received:', data);
            
            // Check if it's a funnel QCM
            if (data && data.questionType === 'funnel-qcm') {
                console.log('🎯 Detected Funnel QCM, loading step by step...');
                
                // Get first available step
                let firstStepNumber = 1;
                
                // If data has step info, find the first available step
                if (data.step) {
                    const steps = Array.isArray(data.step) ? data.step : [data.step];
                    const stepNumbers = steps.map(s => parseInt(s.number)).sort((a, b) => a - b);
                    firstStepNumber = stepNumbers[0] || 1;
                }
                
                console.log(`📍 Loading first step: ${firstStepNumber}`);
                
                // Load the first step using the step endpoint
                await loadFunnelQCMStep(qcmId, firstStepNumber);
                return; // Exit early as loadFunnelQCMStep handles the rest
            }
            
            // Standard QCM loading for non-funnel QCMs
            if (data && data.qcm_id) {
                // Format API : QCM unique
                setQcmData(data);
                // Détecter et mettre à jour le questionType
                if (data.questionType && data.questionType !== currentQuestionType) {
                    setCurrentQuestionType(data.questionType);
                }
                
            // Reset funnel state for standard QCMs
            setCurrentStep(1);
            setCompletedSteps(new Set());
            setTotalSteps(0);
            setAvailableSteps([]);
            setIsLastStep(false);
            setFunnelUserChoices({});
            setLastFunnelResult(null);
            
            // Reset QCM selection states
            setSelectedOption('');
            setDegreeOfCertainty('');
            setFreeTextAnswer('');
            setConceptualMappingAnswers({});
                
            } else if (data && data.questions) {
                // Format ancien : tableau de questions
                setQcmData(data);
                
                // Reset funnel state for standard QCMs
                setCurrentStep(1);
                setCompletedSteps(new Set());
                setTotalSteps(0);
                setAvailableSteps([]);
                setIsLastStep(false);
                
                // Reset QCM selection states
                setSelectedOption('');
                setDegreeOfCertainty('');
                setFreeTextAnswer('');
                setConceptualMappingAnswers({});
            } else {
                throw new Error('Format de données QCM non reconnu');
            }
            
            setCurrentQCMIndex(0);
            setDisplayMode('question');
            setFeedback(null);
            setShowSummary(false);
            setShowKnowledgeReinforcement(false);
            setAttemptCount(1);
            setIsTimerRunning(true);
            
            // Initialize navigation options
            updateNavigationOptions();

            // Reset priming state for new QCM
            primedQcmIdRef.current = null;
            setQcmPrimingMessages([]);
            setQcmSlotId(null);

            if (markInitialSnapResolved) {
                initialSnapResolvedRef.current = true;
            }
            
        } catch (err) {
            console.error('Error loading QCM content:', err);
            setError(err.message || 'Erreur lors du chargement du QCM');
        } finally {
            setLoading(false);
        }
    }, [currentQuestionType, loadFunnelQCMStep, updateNavigationOptions]);

    // --- Get Next QCM ---
    const nextQcmInFlightRef = useRef(false);
    const getNextQCM = useCallback(async () => {
        if (nextQcmInFlightRef.current) return;
        try {
            nextQcmInFlightRef.current = true;
            setError(null);
            
            console.log('=' * 80);
            console.log('🚀 DÉBUT - getNextQCM (QCMContext)');
            console.log('=' * 80);
            console.log('📋 Contexte de la fonction:');
            console.log(`   - sessionContext:`, sessionContext);
            console.log(`   - sessionContext.strategy: ${sessionContext.strategy}`);
            console.log(`   - sessionContext.exam: ${sessionContext.exam}`);
            console.log(`   - sessionContext.user: ${sessionContext.user}`);

            const mergedRevision = getMergedRevisionStrategyFromStorage(
                sessionContext.user,
                sessionContext.exam,
                sessionContext.sessionId || '111222'
            );
            const revisionQuestionType =
                (mergedRevision?.studyMode || 'qcm') === 'flashcard' ? 'FlashCard_Tips' : 'qcm';

            if (mergedRevision.filterType === 'left_topics') {
                const sid = sessionContext.sessionId || '111222';
                const pickedId = await pickQcmIdForUnderpracticedTopics({
                    examId: sessionContext.exam,
                    userId: sessionContext.user,
                    sessionId: sid,
                    excludeQcmId: qcmData?.qcm_id || null,
                    questionType: revisionQuestionType,
                });
                if (pickedId) {
                    await loadQCMContent(pickedId, { markInitialSnapResolved: true });
                    setFeedback(null);
                    setShowSummary(false);
                    setShowKnowledgeReinforcement(false);
                    setDisplayMode('question');
                    setAttemptCount(1);
                    setIsTimerRunning(true);
                    return;
                }
                applyRandomRevisionStrategyWhenLeftTopicsExhausted(
                    sessionContext.user,
                    sessionContext.exam,
                    sessionContext.sessionId || '111222'
                );
                window.dispatchEvent(
                    new CustomEvent('apprentissage:set-revision-strategy', {
                        detail: { filterType: 'random', studyMode: mergedRevision.studyMode || 'qcm' },
                    })
                );
                if (
                    shouldShowLeftTopicsRandomFallbackNotice(
                        sessionContext.user,
                        sessionContext.exam
                    )
                ) {
                    window.dispatchEvent(
                        new CustomEvent('apprentissage:strategy-notice', {
                            detail: { message: LEFT_TOPICS_RANDOM_FALLBACK_NOTICE_EN },
                        })
                    );
                }
            }

            if (
                mergedRevision.filterType === 'manual_task' &&
                Array.isArray(mergedRevision.taskSelection) &&
                mergedRevision.taskSelection.length > 0
            ) {
                try {
                    const data = await postRevisionQcm(sessionContext.exam, sessionContext.user, {
                        filter_type: 'manual_task',
                        selection: mergedRevision.taskSelection,
                        question_type: revisionQuestionType,
                    }, {
                        sessionId: sessionContext.sessionId || '111222',
                        excludeQcmId: qcmData?.qcm_id || null,
                    });
                    if (data.success && data.results?.length) {
                        const first = data.results[0];
                        const qid = first?.id != null ? String(first.id) : null;
                        if (qid) {
                            await loadQCMContent(qid, { markInitialSnapResolved: true });
                            setFeedback(null);
                            setShowSummary(false);
                            setShowKnowledgeReinforcement(false);
                            setDisplayMode('question');
                            setAttemptCount(1);
                            setIsTimerRunning(true);
                            return;
                        }
                    }
                } catch (e) {
                    console.warn('getNextQCM manual_task revision failed:', e?.message || e);
                }
                setError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                setFeedback({ type: 'error', message: NO_QUESTION_FOR_STRATEGY_MESSAGE });
                return;
            }

            if (
                mergedRevision.filterType === 'manual_domain' &&
                Array.isArray(mergedRevision.domainSelection) &&
                mergedRevision.domainSelection.length > 0
            ) {
                try {
                    const data = await postRevisionQcm(sessionContext.exam, sessionContext.user, {
                        filter_type: 'manual_domain',
                        selection: mergedRevision.domainSelection,
                        question_type: revisionQuestionType,
                    }, {
                        sessionId: sessionContext.sessionId || '111222',
                        excludeQcmId: qcmData?.qcm_id || null,
                    });
                    if (data.success && data.results?.length) {
                        const first = data.results[0];
                        const qid = first?.id != null ? String(first.id) : null;
                        if (qid) {
                            await loadQCMContent(qid, { markInitialSnapResolved: true });
                            setFeedback(null);
                            setShowSummary(false);
                            setShowKnowledgeReinforcement(false);
                            setDisplayMode('question');
                            setAttemptCount(1);
                            setIsTimerRunning(true);
                            return;
                        }
                    }
                } catch (e) {
                    console.warn('getNextQCM manual_domain revision failed:', e?.message || e);
                }
                setError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                setFeedback({ type: 'error', message: NO_QUESTION_FOR_STRATEGY_MESSAGE });
                return;
            }

            if (mergedRevision.filterType === 'unseen_qcm') {
                try {
                    const data = await postRevisionQcm(sessionContext.exam, sessionContext.user, {
                        filter_type: 'unseen_qcm',
                        question_type: revisionQuestionType,
                        userid: sessionContext.user,
                        session_id: sessionContext.sessionId || '111222',
                    }, {
                        sessionId: sessionContext.sessionId || '111222',
                        excludeQcmId: qcmData?.qcm_id || null,
                    });
                    if (data.success && data.results?.length) {
                        const first = data.results[0];
                        const qid = first?.id != null ? String(first.id) : null;
                        if (qid) {
                            await loadQCMContent(qid, { markInitialSnapResolved: true });
                            setFeedback(null);
                            setShowSummary(false);
                            setShowKnowledgeReinforcement(false);
                            setDisplayMode('question');
                            setAttemptCount(1);
                            setIsTimerRunning(true);
                            return;
                        }
                    }
                } catch (e) {
                    console.warn('getNextQCM unseen_qcm revision failed:', e?.message || e);
                }
                setError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                setFeedback({ type: 'error', message: NO_QUESTION_FOR_STRATEGY_MESSAGE });
                return;
            }

            if (
                mergedRevision.filterType === 'keywords_fuzzy' &&
                Array.isArray(mergedRevision.keywordSelection) &&
                mergedRevision.keywordSelection.length > 0
            ) {
                try {
                    const selection = mergedRevision.keywordSelection
                        .map((k) => String(k || '').trim())
                        .filter(Boolean);
                    const data = await postRevisionQcm(sessionContext.exam, sessionContext.user, {
                        filter_type: 'keywords_fuzzy',
                        selection,
                        question_type: revisionQuestionType,
                        userid: sessionContext.user,
                    }, {
                        sessionId: sessionContext.sessionId || '111222',
                        excludeQcmId: qcmData?.qcm_id || null,
                    });
                    if (data.success && data.results?.length) {
                        const first = data.results[0];
                        const qid = first?.id != null ? String(first.id) : null;
                        if (qid) {
                            await loadQCMContent(qid, { markInitialSnapResolved: true });
                            setFeedback(null);
                            setShowSummary(false);
                            setShowKnowledgeReinforcement(false);
                            setDisplayMode('question');
                            setAttemptCount(1);
                            setIsTimerRunning(true);
                            return;
                        }
                    }
                } catch (e) {
                    console.warn('getNextQCM keywords_fuzzy revision failed:', e?.message || e);
                }
                setError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
                setFeedback({ type: 'error', message: NO_QUESTION_FOR_STRATEGY_MESSAGE });
                return;
            }

            // 🆕 MODIFICATION: Mapping des stratégies vers des codes fonctionnels pour l'API
            // Maintenant insensible à la casse grâce au backend
            const strategyApiMapping = {
                'smart_test1': 'Random',
                'basic_sequential': 'Sequential',
                'difficulty_adaptive': 'Adaptive',
                '2': 'Random',
                '4': 'Random',
                'Random': 'Random',
                'random': 'Random',
                'RANDOM': 'Random'
            };
            
            const functionalStrategy = strategyApiMapping[sessionContext.strategy] || sessionContext.strategy;
            console.log(`\n🎯 Mapping de la stratégie:`);
            console.log(`   - Stratégie utilisateur: "${sessionContext.strategy}"`);
            console.log(`   - Stratégie mappée: "${functionalStrategy}"`);
            console.log(`   - Mapping utilisé: ${strategyApiMapping[sessionContext.strategy] ? 'oui' : 'non (utilise la stratégie brute)'}`);
            
            console.log(`\n📡 Appel de l'API /api/qcm/next/${sessionContext.exam}`);
            console.log(`   - URL: /api/qcm/next/${sessionContext.exam}`);
            
            // Exclure le QCM actuel pour éviter de recharger le même
            const excludeQcmId =
                qcmData?.qcm_id ||
                qcmData?.FlashCard_Tips_id ||
                currentQCM?.qcm_id ||
                currentQCM?.FlashCard_Tips_id ||
                null;
            // Guard against mode drift: when UI strategy is QCM, always request QCM items.
            const params = {
                strategy: functionalStrategy,
                user: sessionContext.user,
                questionType: revisionQuestionType
            };
            if (excludeQcmId) {
                params.exclude = excludeQcmId;
            }
            
            console.log(`   - Paramètres: strategy=${functionalStrategy}, user=${sessionContext.user}, questionType=${revisionQuestionType}, exclude=${excludeQcmId || 'none'}`);
            
            const response = await nodeApi.get(`/api/qcm/next/${encodeExamIdForUrl(sessionContext.exam)}`, {
                params,
            });

            console.log(`\n✅ Réponse de l'API reçue:`);
            console.log(`   - success: ${response.data.success}`);
            console.log(`   - strategy: ${response.data.strategy}`);
            console.log(`   - normalized_strategy: ${response.data.normalized_strategy}`);
            console.log(`   - total_available: ${response.data.total_available}`);
            console.log(`   - qcm_id: ${response.data.qcm?.qcm_id}`);

            if (response.data.success) {
                console.log(`\n🔄 Chargement du nouveau QCM...`);
                console.log(`   - QCM ID à charger: ${response.data.qcm.qcm_id}`);
                
                // Charger le nouveau QCM
                await loadQCMContent(response.data.qcm.qcm_id, { markInitialSnapResolved: true });
                
                console.log(`✅ Nouveau QCM chargé avec succès`);
                console.log(`   - Affichage remis à jour`);
                console.log(`   - Feedback réinitialisé`);
                console.log(`   - Mode affichage: question`);
                console.log(`   - Tentative: 1`);
                console.log(`   - Timer redémarré`);
                
                setFeedback(null);
                setShowSummary(false);
                setShowKnowledgeReinforcement(false);
                setDisplayMode('question');
                setAttemptCount(1);
                setIsTimerRunning(true);
            } else {
                console.log(`❌ L'API a retourné success: false`);
                console.log(`   - Message: ${response.data.message}`);
                setError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
            }
        } catch (error) {
            console.error('❌ Erreur dans getNextQCM:', error);
            console.error('   - Type d\'erreur:', error.constructor.name);
            console.error('   - Message:', error.message);
            if (error.response) {
                console.error('   - Status:', error.response.status);
                console.error('   - Data:', error.response.data);
            }
            setError(NO_QUESTION_FOR_STRATEGY_MESSAGE);
            setFeedback({ type: 'error', message: NO_QUESTION_FOR_STRATEGY_MESSAGE });
        } finally {
            nextQcmInFlightRef.current = false;
            console.log('=' * 80);
            console.log('✅ FIN - getNextQCM (QCMContext)');
            console.log('=' * 80);
        }
    }, [sessionContext, loadQCMContent, t, currentQuestionType]);

    // --- Go to Next Funnel Step ---
    const goToNextFunnelStep = useCallback(async () => {
        if (!currentQCM) return;
        
        try {
            // Mark current step as completed
            setCompletedSteps(prev => new Set([...prev, currentStep]));
            
            if (isLastStep) {
                // If it's the last step, go to next QCM
                console.log('🏁 Last step completed, getting next QCM...');
                await getNextQCM();
            } else {
                // Go to next available step
                const nextStepNumber = availableSteps.find(step => step > currentStep);
                if (nextStepNumber) {
                    console.log(`➡️ Moving to next step: ${nextStepNumber}`);
                    await loadFunnelQCMStep(currentQCM.qcm_id, nextStepNumber);
                } else {
                    console.warn('⚠️ No next step found, getting next QCM...');
                    await getNextQCM();
                }
            }
            
            // Reset UI state for new step
            setDisplayMode('question');
            setFeedback(null);
            setSelectedOption('');
            setDegreeOfCertainty('');
            
        } catch (error) {
            console.error('Error going to next funnel step:', error);
            setError('Erreur lors du passage à l\'étape suivante');
        }
    }, [currentQCM, currentStep, isLastStep, availableSteps, getNextQCM, loadFunnelQCMStep]);

    // --- Initial QCM Loading ---
    const [isLoadingFromSearch, setIsLoadingFromSearch] = useState(false);

    const loadQCMContentRef = useRef(loadQCMContent);
    loadQCMContentRef.current = loadQCMContent;

    const loadedQcmIdForInitialCheck = useMemo(() => {
        if (!qcmData) return null;
        const id =
            qcmData.qcm_id ??
            (Array.isArray(qcmData.questions) && qcmData.questions[0]?.qcm_id) ??
            null;
        return id != null ? String(id) : null;
    }, [qcmData]);

    /**
     * Charger le QCM d’entrée (ex. filtre revision_qcm depuis SmartExamRouter).
     * Ne pas dépendre de loadQCMContent (son identité change quand currentQuestionType change) :
     * sinon boucle infinie de rechargements / scintillement.
     */
    useEffect(() => {
        if (!initialQcmId) {
            return;
        }
        if (isLoadingFromSearch) {
            return;
        }
        if (initialSnapResolvedRef.current) {
            return;
        }

        if (loadedQcmIdForInitialCheck != null && loadedQcmIdForInitialCheck === String(initialQcmId)) {
            initialSnapResolvedRef.current = true;
            return;
        }

        console.log('🚀 Loading initial QCM with ID:', initialQcmId, {
            previousId: loadedQcmIdForInitialCheck,
        });
        loadQCMContentRef.current(initialQcmId);
    }, [initialQcmId, loadedQcmIdForInitialCheck, isLoadingFromSearch]);

    // Écouter les événements de chargement de QCM depuis la recherche
    useEffect(() => {
        const handleLoadSpecificQCMFromSearch = async (event) => {
            const { qcmId } = event.detail;
            console.log('🔍 Événement loadSpecificQCMFromSearch reçu avec ID:', qcmId);
            
            // Utiliser le système de chargement existant !
            if (qcmId) {
                try {
                    // Empêcher le rechargement de l'initialQcmId pendant qu'on charge depuis la recherche
                    console.log('🛡️ Activation du flag isLoadingFromSearch=true');
                    setIsLoadingFromSearch(true);
                    
                    console.log('🎯 Utilisation du système existant loadQCMContent:', qcmId);
                    
                    // ✅ UTILISER LE CODE EXISTANT qui fonctionne !
                    await loadQCMContent(qcmId, { markInitialSnapResolved: true });
                    
                    console.log('🎊 QCM chargé via le système existant !');
                    
                    // Réactiver le chargement automatique après un délai
                    setTimeout(() => {
                        setIsLoadingFromSearch(false);
                    }, 1000);
                    
                } catch (error) {
                    console.error('❌ Erreur lors du chargement du QCM:', error);
                    setError('Erreur lors du chargement du QCM');
                    setLoading(false);
                    setIsLoadingFromSearch(false); // Réactiver en cas d'erreur
                }
            }
        };

        window.addEventListener('loadSpecificQCMFromSearch', handleLoadSpecificQCMFromSearch);
        
        return () => {
            window.removeEventListener('loadSpecificQCMFromSearch', handleLoadSpecificQCMFromSearch);
        };
    }, []); // Pas de dépendances pour éviter les re-créations


    // --- Get Similar QCM ---
    const getSimilarQCM = useCallback(() => {
        if (currentQCM?.similar_qcm) {
            // Créer un QCM temporaire avec les données du QCM similaire
            const similarQCMData = {
                ...currentQCM,
                qcm: currentQCM.similar_qcm,
                qcm_id: `similar_${currentQCM.qcm_id}`,
                theme: currentQCM.similar_qcm.theme || currentQCM.theme,
                context: currentQCM.similar_qcm.context || currentQCM.context
            };
            
            setQcmData(similarQCMData);
            setFeedback(null);
            setShowSummary(false);
            setShowKnowledgeReinforcement(false);
            setDisplayMode('question');
            setAttemptCount(1);
            setIsTimerRunning(true);
            
            // Remettre à jour les options de navigation pour le nouveau QCM
            setOptions([
                { id: 'qcm', label: 'Options', action: () => {
            setDisplayMode('qcm');
            setDegreeOfCertainty('');
        }},
                { id: 'solution', label: 'Solution', action: () => setDisplayMode('solution') },
                { id: 'help', label: 'Aide', action: () => setDisplayMode('help') }
            ]);
        }
    }, [currentQCM]);

    // --- Question Type Management ---
    const changeQuestionType = useCallback(async (newQuestionType) => {
        if (newQuestionType !== currentQuestionType) {
            setCurrentQuestionType(newQuestionType);
            
            // Recharger un QCM avec le nouveau type
            try {
                setLoading(true);
                const functionalStrategy = 'Random'; // Utiliser Random par défaut pour le changement de mode
                
                const response = await nodeApi.get(`/api/qcm/next/${encodeExamIdForUrl(sessionContext.exam)}`, {
                    params: {
                        strategy: functionalStrategy,
                        user: sessionContext.user,
                        questionType: newQuestionType
                    }
                });

                if (response.data.success) {
                    await loadQCMContent(response.data.qcm.qcm_id, { markInitialSnapResolved: true });
                    setFeedback(null);
                    setShowSummary(false);
                    setShowKnowledgeReinforcement(false);
                    setDisplayMode('question');
                    setAttemptCount(1);
                } else {
                    setError(`Aucun QCM de type "${newQuestionType}" trouvé pour cet examen`);
                }
            } catch (error) {
                console.error('Error changing question type:', error);
                setError('Erreur lors du changement de mode');
            } finally {
                setLoading(false);
            }
        }
    }, [currentQuestionType, sessionContext, loadQCMContent]);

    // --- QCM Navigation ---
    const goToNextQCM = useCallback(() => {
        // Pour format avec plusieurs questions
        if (qcmData && qcmData.questions && currentQCMIndex < qcmData.questions.length - 1) {
            setCurrentQCMIndex(prev => prev + 1);
            setFreeTextAnswer('');
            setSelectedOption('');
            setDegreeOfCertainty('');
            setConceptualMappingAnswers({});
            setDisplayMode('question');
            setFeedback(null);
            setAttemptCount(1);
            setElapsedTime(0);
            elapsedTimeRef.current = 0;
            setIsTimerRunning(true);
        }
        // Pour format QCM unique, on pourrait charger un nouveau QCM aléatoire
        // TODO: Implémenter la navigation vers un nouveau QCM pour le format unique
    }, [qcmData, currentQCMIndex]);

    // --- Free Text Answer Submission ---
    const submitFreeTextAnswer = useCallback(async (answer) => {
        if (!currentQCM || !answer.trim()) return;

        // Ensure priming is done and get a 2-message history immediately (avoid state race)
        let historyToSend = qcmPrimingMessages.slice(0, 2);
        if (historyToSend.length < 2) {
            const primingResult = await primeQcmContext();
            if (primingResult && primingResult.messages && primingResult.messages.length >= 2) {
                historyToSend = primingResult.messages.slice(0, 2);
            }
        }

        // Build a strict YES/NO directive so /api/evaluation can classify correctly
        const yesNoDirective = {
            role: 'user',
            content: "For the next Learner solution, respond strictly with YES if it fully satisfies ALL primingInstruction requirements without any missing required component or qualifier; otherwise respond strictly with NO. Do not add any other text."
        };

        const historyWithDirective = [...historyToSend, yesNoDirective];

        // Respect Endpoint: /api/evaluation
        const evaluationPayload = {
            userId: sessionContext.user,
            sessionId: sessionContext.sessionId,
            examId: sessionContext.exam,
            instruction_id: currentQCM.qcm_id,
            sub_instruction_id: currentQCM.qcm_id,
            student_answer: answer.trim(),
            examType: 'qcm',
            context: sessionContext, // Ajout de la clé context dans le payload
            slot_id: qcmSlotId,
            conversation_history: historyWithDirective
        };

        try {
            setEvaluationLoading(true);
            const response = await evaluateAnswer(evaluationPayload);
            
            // Store LLM evaluation for display (both correct and incorrect answers)
            setQcmData(prev => {
                if (prev?.questions) {
                    // Format ancien (tableau de questions)
                    const updatedQuestions = [...prev.questions];
                    updatedQuestions[currentQCMIndex] = {
                        ...updatedQuestions[currentQCMIndex],
                        llmEvaluation: {
                            is_correct: response.evaluation?.is_correct,
                            score: response.evaluation?.is_correct ? 100 : 0,
                            threshold: 70,
                            explanation: response.evaluation?.explanation || response.evaluation?.result
                        }
                    };
                    return { ...prev, questions: updatedQuestions };
                } else {
                    // Format nouveau (QCM unique)
                    return {
                        ...prev,
                        llmEvaluation: {
                            is_correct: response.evaluation?.is_correct,
                            score: response.evaluation?.is_correct ? 100 : 0,
                            threshold: 70,
                            explanation: response.evaluation?.explanation || response.evaluation?.result
                        }
                    };
                }
            });

            if (response.success && response.evaluation?.is_correct) {
                // Clear priming state after correct (slot is released server-side)
                setQcmPrimingMessages([]);
                setQcmSlotId(null);
                setFeedback({ type: 'success', message: t('correct_answer') });
                setIsTimerRunning(false);
                setDegreeOfCertainty('');
                setShowSummary(true);
                setShowKnowledgeReinforcement(true);
                setDisplayMode('solution'); // Show solution view like for correct QCM answers
                
                // Show navigation buttons like in Lab
                setOptions([
                    { id: 'similar_qcm', label: 'QCM Similaire', action: getSimilarQCM },
                    { id: 'next_qcm', label: t('next_qcm') || 'QCM Suivant', action: getNextQCM }
                ]);
            } else {
                setFeedback({ 
                    type: 'error', 
                    message: (response.evaluation && response.evaluation.explanation) || t('incorrect_answer') 
                });
                setAttemptCount(prev => prev + 1);
            }

            // Trigger scoring API
            await triggerScoring({
                questionType: 'qcm_free_text',
                student_answer: answer,
                is_correct: response.success && response.evaluation?.is_correct
            });

        } catch (err) {
            console.error('Error evaluating free text answer:', err);
            setFeedback({ type: 'error', message: err.message || t('evaluation_error') });
        } finally {
            setEvaluationLoading(false);
        }
    }, [currentQCM, sessionContext, attemptCount, t]);

    // --- QCM Option Submission ---
    const submitQCMAnswer = useCallback(async (selectedAnswer, certaintyDegree) => {
        if (!currentQCM || !selectedAnswer) return;

        // Support both standard and funnel QCM structures
        const qcmNode = (currentQCM && (currentQCM.qcm || (currentQCM.step && currentQCM.step.qcm))) || currentQCM;

        // Handle both single answer and multiple answers (array)
        const selectedAnswers = Array.isArray(selectedAnswer) ? selectedAnswer : [selectedAnswer];

        // Build a set of correct answers from various possible shapes
        const correctAnswerSet = (() => {
            const answers = [];
            
            // Format MongoDB : correct_answer peut être un array ou un objet simple
            if (qcmNode && Array.isArray(qcmNode.correct_answer)) {
                // Format array : [{ answer: "...", explanation: "..." }, ...]
                for (const entry of qcmNode.correct_answer) {
                    if (typeof entry === 'string') answers.push(entry);
                    else if (entry && typeof entry.answer === 'string') answers.push(entry.answer);
                }
            } else if (qcmNode && qcmNode.correct_answer && typeof qcmNode.correct_answer.answer === 'string') {
                // Format objet simple : { answer: "...", explanation: "..." }
                answers.push(qcmNode.correct_answer.answer);
            }
            
            // Fallback pour correct_answers (au pluriel)
            if (qcmNode && Array.isArray(qcmNode.correct_answers)) {
                for (const entry of qcmNode.correct_answers) {
                    if (typeof entry === 'string') answers.push(entry);
                    else if (entry && typeof entry.answer === 'string') answers.push(entry.answer);
                }
            }
            
            return new Set(answers.filter(Boolean));
        })();

        // Check if all selected answers are correct and all correct answers are selected
        const selectedSet = new Set(selectedAnswers);
        const allSelectedCorrect = selectedAnswers.every(answer => correctAnswerSet.has(answer));
        const allCorrectSelected = [...correctAnswerSet].every(answer => selectedSet.has(answer));
        const isCorrect = allSelectedCorrect && allCorrectSelected;

        // Trigger scoring with degree of certainty and new format
        await triggerScoring({
            questionType: 'qcm',
            student_answer: selectedAnswers, // Send array of answers
            is_correct: isCorrect,
            degreeOfCertainty: certaintyDegree,
            correct_answers: [...correctAnswerSet].map(answer => ({ answer })) // New format for backend
        });

        if (isCorrect) {
            setIsTimerRunning(false);
            setDegreeOfCertainty('');
            setFeedback({ type: 'success', message: t('correct_answer') });
            setShowSummary(true);
            setShowKnowledgeReinforcement(true);
            setDisplayMode('solution');
            // Show navigation buttons like in Lab
            // Si c'est un QCM similaire (qcm_id commence par "similar_"), on n'affiche que "QCM Suivant"
            const isSimilarQCM = currentQCM?.qcm_id?.startsWith('similar_');
            setOptions([
                ...(isSimilarQCM ? [] : [{ id: 'similar_qcm', label: 'QCM Similaire', action: getSimilarQCM }]),
                { id: 'next_qcm', label: t('next_qcm') || 'QCM Suivant', action: getNextQCM }
            ]);
        } else {
            setFeedback({ type: 'error', message: t('incorrect_answer') });
            setAttemptCount(prev => prev + 1);
            // Show navigation options for incorrect answer
            setOptions([
                { id: 'solution', label: t('solution') || 'Solution', action: () => setDisplayMode('solution') },
                { id: 'help', label: t('help') || 'Aide', action: () => setDisplayMode('help') }
            ]);
        }

    }, [currentQCM, sessionContext, t, getNextQCM]);

    // --- Conceptual Mapping Submission ---
    const submitConceptualMapping = useCallback(async (mappingId, userAnswer) => {
        const mapping = currentQCM?.conceptual_Mapping?.find(cm => cm.conceptual_mapping_id === mappingId);
        if (!mapping) return;

        const isCorrect = userAnswer === mapping.conceptual_mapping_solution;

        // Trigger scoring for conceptual mapping
        await triggerScoring({
            questionType: 'conceptual_mapping',
            student_answer: userAnswer,
            is_correct: isCorrect,
            mapping_id: mappingId
        });

        // Update conceptual mapping answers
        setConceptualMappingAnswers(prev => ({
            ...prev,
            [mappingId]: { answer: userAnswer, isCorrect }
        }));

    }, [currentQCM, sessionContext]);

    // --- Scoring API Integration ---
    const triggerScoring = useCallback(async (scoringData) => {
        const expectedTime = maxTime > 0 ? maxTime : null;
        
        // Handle multiple answers for exam style QCM
        const studentAnswer = Array.isArray(scoringData.student_answer) 
            ? scoringData.student_answer.join('; ') 
            : scoringData.student_answer;
        
        const payload = {
            exam: sessionContext.exam,
            user: sessionContext.user,
            sessionId: sessionContext.sessionId,
            exercise_id: currentQCM?.qcm_id,
            instruction_id: currentQCM?.qcm_id,
            sub_instruction_id: currentQCM?.qcm_id,
            questionType: scoringData.questionType, // Utiliser le questionType réel
            student_answer: studentAnswer,
            is_correct: scoringData.is_correct,
            nb_attempt: attemptCount,
            elapsed_time: elapsedTimeRef.current,
            expected_time: expectedTime,
            degreeOfCertainty: scoringData.degreeOfCertainty || 0, // Optionnel avec valeur par défaut
            context: sessionContext, // Ajout de la clé context dans le payload
            ...(scoringData.mapping_id && { mapping_id: scoringData.mapping_id }),
            // New format for multiple correct answers
            ...(scoringData.correct_answers && { correct_answers: scoringData.correct_answers })
        };

        // Ancien système de scoring supprimé - utilise maintenant le nouveau système dans MobileQCM.js
        console.log('📡 Ancien système de scoring désactivé - utilise le nouveau système dans MobileQCM.js');
    }, [sessionContext, currentQCM, attemptCount, maxTime]);

    // --- Context Value ---
    const contextValue = {
        // Data
        qcmData,
        currentQCM,
        currentQCMIndex,
        
        // State
        loading,
        evaluationLoading,
        error,
        feedback,
        freeTextAnswer,
        selectedOption,
        degreeOfCertainty,
        conceptualMappingAnswers,
        
        // Funnel QCM State
        currentStep,
        completedSteps,
        totalSteps,
        availableSteps,
        isLastStep,
        
        // Display
        displayMode,
        qcmSolutionContent,
        showSummary,
        showKnowledgeReinforcement,
        
        // Timer
        attemptCount,
        elapsedTime,
        maxTime,
        isTimerRunning,
        
        // Navigation
        options,
        showNavigationButton,
        navigationButtonLabel,
        navigationButtonAction,
        
        // Session
        sessionContext,
        currentQuestionType,
        setError,
        
        // Actions
        setFreeTextAnswer,
        setSelectedOption,
        setDegreeOfCertainty,
        setHoveredCertainty,
        setDisplayMode,
        setIsTimerRunning,
        submitFreeTextAnswer,
        submitQCMAnswer,
        submitConceptualMapping,
        goToNextQCM,
        getNextQCM,
        getSimilarQCM,
        loadQCMContent,
        changeQuestionType,
        funnelUserChoices,
        setFunnelUserChoices,
        lastFunnelResult,
        setLastFunnelResult,
        
        // Funnel QCM Actions
        loadFunnelQCMStep,
        goToNextFunnelStep
    };

    const showBlockingInitialShell =
        Boolean(initialQcmId) &&
        !initialNoQuestionForStrategy &&
        !qcmData &&
        (loading || Boolean(error));

    return (
        <QCMContext.Provider value={contextValue}>
            {children}
            {/* Calque chargement APRÈS MobileQCM : même z-index parent, le dernier frère gagne sans comparaison de z */}
            {showBlockingInitialShell ? (
                <div
                    role={error ? 'alert' : 'status'}
                    aria-live="polite"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: Z_QCM_BLOCKING,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#0f172a',
                        color: error ? '#fecaca' : '#93c5fd',
                        gap: '12px',
                        padding: '24px',
                        textAlign: 'center',
                        maxWidth: '100vw',
                        boxSizing: 'border-box',
                    }}
                >
                    {error ? (
                        <>
                            <p style={{ margin: 0, fontSize: '17px', fontWeight: 600, color: '#fecaca' }}>
                                Unable to load this question
                            </p>
                            <p
                                style={{
                                    margin: '0 0 8px',
                                    fontSize: '13px',
                                    color: '#fca5a5',
                                    wordBreak: 'break-word',
                                    maxWidth: 'min(100%, 420px)',
                                }}
                            >
                                {error}
                            </p>
                            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8', maxWidth: '360px' }}>
                                Check that the search API is reachable, then reload the page or change exam / revision
                                strategy in your profile.
                            </p>
                        </>
                    ) : (
                        <>
                            <span style={{ fontSize: '36px', lineHeight: 1 }} aria-hidden>
                                ⏳
                            </span>
                            <p style={{ margin: 0, fontSize: '15px', fontWeight: 500 }}>Loading question…</p>
                        </>
                    )}
                </div>
            ) : null}
        </QCMContext.Provider>
    );
};

export default QCMProvider;